import { AsgardCloudBaseService } from '../asgard-cloud-base.service';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { DriveMetadata } from '../metadata/DriveMetadata';
import { MimeTypesEnum, ToastrActionEnum, ToastrStringsEnum } from '../../../utils/enums';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TabService } from '../../tab.service';
import { ToastrDialogService } from '../../toastr-dialog.service';
import { FileConvertorService } from '../../file-convertor/file-convertor-service';
import { BlobToPdfService } from '../../blob-to-pdf.service';
import { ModalWindowService } from '../../ui/modal-window.service';
import { DocumentManagerService } from '../../document/document-manager.service';
import { TranslateService } from '@ngx-translate/core';
import { CloudServiceEnum, MetadataTypesEnum, SaveStatusEnum } from '../enums';
import { isK3000domain, parseExtenstion } from '../../../core/common';
// import { CommonMessageDialogComponent } from '../../../components/dialogs/common-message-dialog/common-message-dialog.component';
import { ExtraCopyDialogComponent } from '../../../components/dialogs/extra-copy-dialog/extra-copy-dialog.component';
import { FileCopyInfo } from '../models/file-copy-info';
import { CloudData } from '../metadata/CloudData';
import { FileData } from '../../../../models/FileData';
import { CloudException, CloudExceptionEnum } from '../models/exceptions';
import { MatDialog } from '@angular/material/dialog';
import {
  FileMovingWarningDialogComponent
} from 'src/app/components/dialogs/file-moving-warning-dialog/file-moving-warning-dialog.component';
import { GdriveApiService } from './gdrive-api.service';
import { GapiSession } from './gapi.session';
import { CloudFoldersEnum } from 'src/app/utils/enums/cloud-folders.enum';
import { getMimeTypeEnumFromString, validateMimetypes } from '../common';
import { CloudRecentFilesService } from '../cloud-recent-files.service';
import { ExamModeRestrictionsService } from '../../exam/exam-mode-restrictions.service';
import { AccountService } from '../../auth/account.service';
import { LicenseService } from '../../license.service';
import { OpenFileService } from '../../open-file.service';
import { EpubFileService } from '../../epub/epub-file.service';
import { AsgardDocEpub } from 'src/app/core/documents/asgard-doc-epub';

declare var UploaderForGoogleDrive;

interface GDriveSaveFileError {
  error: {
    errors: {
      domain: string;
      reason: string;
      message: string;
    }[],
    code: number;
    message: string;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AsgardCloudGdriveService extends AsgardCloudBaseService {
  @Output() SaveToDrive: EventEmitter<boolean> = new EventEmitter();

  private file: File;
  private uploadInfo;
  private metadataList: DriveMetadata[] = [];

  constructor(
    tabService: TabService,
    convertorService: FileConvertorService,
    modalService: ModalWindowService,
    docMgrService: DocumentManagerService,
    toastrService: ToastrDialogService,
    blobToPdfService: BlobToPdfService,
    epubFileService: EpubFileService,
    translateService: TranslateService,
    examModeService: ExamModeRestrictionsService,
    dialog: MatDialog,
    accountService: AccountService,
    licenseService: LicenseService,
    openFileService: OpenFileService,
    private readonly http: HttpClient,
    private readonly sessionService: GapiSession,
    private readonly gdriveApi: GdriveApiService,
    private readonly cloudRecentFilesService: CloudRecentFilesService,
  ) {
    super(
      tabService, convertorService, modalService, docMgrService,
      toastrService, blobToPdfService, epubFileService, translateService, examModeService,
      dialog, accountService, licenseService, openFileService
    );
  }

  async getFiles(currentFolder: string, showAllFiles: boolean,
    dialogStatus: SaveStatusEnum, folderToShow: CloudFoldersEnum | undefined): Promise<CloudData[]> {
    let cloudDataList: CloudData[] = [];
    this.metadataList = [];

    if (folderToShow && folderToShow === CloudFoldersEnum.Shared && currentFolder === this.ROOT_FOLDER) {
      this.gdriveApi.getSharedDrives()
        .then(x => x.forEach(item =>
          cloudDataList.unshift({
            id: item.id || '',
            name: item.name || '',
            extension: '',
            mimeType: MimeTypesEnum.SharedDrive,
            modifiedTime: item.createdTime || new Date().toJSON(),
            isShared: true,
            size: '',
            path: item.id || '',
            parents: ''
          })
        ));
    }

    // get the files

    if (folderToShow && folderToShow === CloudFoldersEnum.Recent) {
      const recentFiles = await this.cloudRecentFilesService.getFiles(CloudServiceEnum.GoogleDrive) as DriveMetadata[];

      recentFiles.forEach((item: DriveMetadata) => {
        const cloudData = this.mapMetadataToCloudData(item);

        cloudDataList.push(cloudData);
        this.metadataList.push(item);
      });
    } else {
      const files = await this.gdriveApi.getFiles(currentFolder, folderToShow);

      if (!files) {
        return [];
      }

      files.forEach((item: gapi.client.drive.File) => {
        const cloudData = this.mapMetadataToCloudData(item as DriveMetadata);

        (item as DriveMetadata).type = MetadataTypesEnum.DriveMetadata;
        cloudDataList.push(cloudData);
        this.metadataList.push(item as DriveMetadata); // Keep track of original metadata
      });
    }

    if (!showAllFiles || dialogStatus === SaveStatusEnum.Ocr) {
      cloudDataList = this.filterUnsupportedFiles(cloudDataList, dialogStatus);
    }

    return cloudDataList;
  }

  async getFileMetadata(fileId: string): Promise<Partial<CloudData> | null> {
    const file: gapi.client.drive.File | null = await this.gdriveApi.getFile(fileId)
      .catch((error) => {
        console.error(error);
        return null;
      });

    return file !== null ? {
      id: file.id,
      name: file.name,
      isShared: file.shared,
    } as Partial<CloudData> : null;
  }

  async newFolder(name: string, cloudData: CloudData | null): Promise<gapi.client.drive.File | null> {
    return await this.gdriveApi.newFolder(name.trim(), cloudData === null ? this.ROOT_FOLDER : cloudData.id)
      .catch((error) => {
        console.error(error);
        this.displayGeneralError();
        return null;
      });
  }

  async uploadToCloud(parentFolder: CloudData | null, filename: string, file: File | null = null): Promise<void> {
    if ((!file || file.type !== MimeTypesEnum.Mp3) && this.tabService.activeTab) {
      if (filename === undefined || filename === '') {
        filename = this.tabService.activeTab.title;
      }
      if (filename !== this.tabService.activeTab.title) {
        this.tabService.activeTab.setTabTitle(filename);
      }

      if (!file) {
        if (this.docMgrService.currentDoc instanceof AsgardDocEpub) {
          file = await this.epubFileService.exportWithAnnotations();
        } else {
          file = await this.blobToPdfService.getCurrentPDF();
        }

        // getCurrentPDF() can return null. if it does, do not continue.
        if (!file) {
          this.displayGeneralError();
          return;
        }
      }
    }

    let parentFolderId = parentFolder ? parentFolder.id : this.gdriveApi.ROOT_FOLDER;

    // If audiomaker && destination is root, relay to or create audiomaker folder
    if (file && file.type === MimeTypesEnum.Mp3 && parentFolderId === this.gdriveApi.ROOT_FOLDER) {
      parentFolderId = await this.createOrAddAudiomakerFolder() || '';
    }

    if (file && parentFolderId) {
      const fileData: FileData = this.checkFilenameBeforeUpload(file, filename, parentFolderId);

      this.upload(fileData);
    }
  }

  async createOrAddAudiomakerFolder(): Promise<string | null | undefined> {
    const foldername = isK3000domain() ? this.K3000_AUDIOMAKER_FOLDER : this.ALINEA_AUDIOMAKER_FOLDER;
    return await this.gdriveApi.getFolderId(foldername)
      .catch((error) => {
        console.error(error);
        this.displayGeneralError();
        return null;
      });
  }

  saveAudiofile(audiofile: File, currentFolder: CloudData | null): void {
    this.uploadToCloud(currentFolder, audiofile.name, audiofile);
  }

  async copyOrMoveFile(fileCopyInfo: FileCopyInfo): Promise<gapi.client.drive.File | null> {
    try {
      // if you don't have any copy/move info
      if (fileCopyInfo === null || fileCopyInfo.fileToCopy === null) {
        return null;
      }


      const parentId = fileCopyInfo.parentToCopy ? fileCopyInfo.parentToCopy.id : 'root';
      // if action is to copy
      if (fileCopyInfo.copy) {
        // if you try to copy a folder
        if (fileCopyInfo.mimeType === MimeTypesEnum.DriveFolder || fileCopyInfo.mimeType === MimeTypesEnum.Folder) {
          await this.gdriveApi.copyFolder(fileCopyInfo.filename, fileCopyInfo.fileToCopy.id, parentId);
        }
        // if you try to copy a file
        else {
          return await this.gdriveApi.copyFile(fileCopyInfo.fileToCopy.id, parentId);
        }
      }
      
      // if action is to move
      else {
          const fileCopyParentId = fileCopyInfo.fileToCopy.parents ? fileCopyInfo.fileToCopy.parents : 'root';

          this.checkFileMovingPath(fileCopyParentId, parentId);

          const { fileToCopy: { isShared: fileShared } } = fileCopyInfo;

          const newParentShared = fileCopyInfo.parentToCopy ? fileCopyInfo.parentToCopy.isShared : false;
          const newParentName = fileCopyInfo.parentToCopy ? fileCopyInfo.parentToCopy.name : 'root';

          const fileCurrentParent = await this.getFileMetadata(fileCopyParentId);
          const newParentId = fileCopyInfo.fileToCopy.parents ? fileCopyInfo.fileToCopy.parents : fileCopyInfo.fileToCopy.path;
          const { isShared: currentParentShared, name: currentParrentName } = fileCurrentParent as Partial<CloudData>;

          if (fileShared && (newParentShared || currentParentShared)) {

            if (fileCopyInfo.fileToCopy) {

              if (fileShared && (newParentShared || currentParentShared)) {
                const messages: { text: string, folderName: string }[] = [];

                if (newParentShared) {
                  messages.push({
                    text: `MOVEDLG.SHARED_FOLDERS_WARNING.GET_ACCESS`,
                    folderName: `'${newParentName}'`,
                  });
                }

                if (currentParentShared) {
                  messages.push({
                    text: `MOVEDLG.SHARED_FOLDERS_WARNING.LOSE_ACCESS`,
                    folderName: `'${currentParrentName}'`,
                  });
                }

                const result = await this.dialog
                  .open(FileMovingWarningDialogComponent, {
                    id: 'warning-dlg',
                    data: {
                      messages,
                    }
                  })
                  .afterClosed().toPromise();

                if (result) {
                  return await this.gdriveApi.moveFile(fileCopyInfo.fileToCopy.id, parentId, newParentId);
                }
              }
            }
          } else {
            return await this.gdriveApi.moveFile(fileCopyInfo.fileToCopy.id, parentId, newParentId);
          }
      }

      // if somehow action is not copy and not move
      return null;
    } catch (err) {
      let messageKey = `CLOUDERRORS.FILE_${fileCopyInfo.copy ? 'COPY' : 'MOVE'}`;

      if (err instanceof CloudException) {
        messageKey = err.toString();
      }

      // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey });
      this.toastrService.showToaster(ToastrActionEnum.Error, this.translateService.instant(messageKey));
      return null;
    }
  }

  async updateFile(file: CloudData, params: DriveMetadata): Promise<gapi.client.drive.File | null> {
    const gDriveFile: gapi.client.drive.File = {};
    if (params.name) { gDriveFile.name = params.name; }
    if (params.id) { gDriveFile.id = params.id; }
    if (params.mimeType) { gDriveFile.mimeType = params.mimeType; }
    if (params.parents) { gDriveFile.parents = [params.parents]; }
    if (params.size) { gDriveFile.size = `${params.size}`; }
    if (params.shared) { gDriveFile.shared = params.shared; }
    if (params.modifiedTime) { gDriveFile.modifiedTime = params.modifiedTime; }

    if (gDriveFile.id) {
      this.cloudRecentFilesService.removeFileFromRecent(gDriveFile.id, CloudServiceEnum.GoogleDrive);
    }

    const updatedFile = await this.gdriveApi.updateFile(file.id, gDriveFile)
      .catch((error) => {
        console.error(error);
        this.displayGeneralError();
        return null;
      });

    if (updatedFile) {
      this.cloudRecentFilesService.addFileToRecent(updatedFile as DriveMetadata, CloudServiceEnum.GoogleDrive);
    }


    return updatedFile;
  }

  async deleteFile(file: CloudData): Promise<gapi.client.drive.File> {
    return await this.gdriveApi.deleteFile(file.id);
  }

  async getFileFromCloud(fileId: string, dialogStatus: SaveStatusEnum, paneIndex: number): Promise<void> {
    const metadata = this.getMetadata(fileId);
    if (!metadata) {
      return;
    }

    this.getFile(fileId, dialogStatus)
      .then((file) => {
        this.file = file;
        this.processFiles(this.file, dialogStatus, metadata, paneIndex);
        this.cloudRecentFilesService.addFileToRecent(metadata, CloudServiceEnum.GoogleDrive);
      })
      .catch((error) => {
        console.error(error);
        if (error !== 'different user') {
          this.toastrService.hideToastr(metadata.name);
          this.displayGeneralError();
        }
      });
  }

  getMetadata(fileId: string): DriveMetadata | undefined {
    return this.metadataList.find(x => x.id === fileId);
  }

  private async upload(fileData: FileData): Promise<void> {
    const parentId = fileData.path ? [fileData.path] : [];
    const auth = await this.sessionService.getUserToken();
    const file = fileData.file;

    if (file.type !== MimeTypesEnum.Mp3) { // ignore setting metadata if mp3
      if (this.tabService.activeTab && this.tabService.activeTab.gDriveMetadata) {
        this.driveMetadata = this.tabService.activeTab.gDriveMetadata;
      }
    }

    // metadata is derived from uploadInfo
    this.uploadInfo = {
      file,
      token: auth,
      parents: parentId,
      fileId: undefined,
      onComplete: this.onImportComplete,
      onError: this.onImportFailed,
      params: {
        convert: false,
        ocr: false,
        supportsAllDrives: true,
      },
    };

    if (file.type === MimeTypesEnum.Mp3) { // Don't mess with save (as) and metadata if mp3
      this.uploadFile();
      return;
    } else if (!this.getSaveAs()) {
      // adding ID to uploadInfo tells the uploader to overwrite the file on google drive.
      if (this.driveMetadata !== undefined && this.driveMetadata !== null && this.driveMetadata.name === fileData.filename) {
        this.uploadInfo.fileId = this.driveMetadata.id;
      }
      this.uploadFile();
    } else {
      for (const fileMeta of this.metadataList) {
        if ((fileMeta.name === fileData.filename
            || fileMeta.name + '.pdf' === fileData.filename)
            && fileMeta.parents
            && (fileMeta.parents.indexOf(fileData.path) > -1
            || fileData.path === 'root')) {
          this.modalService.createModalWindow(ExtraCopyDialogComponent);
          return;
        }
      }
      this.uploadFile();
    }
  }

  uploadFile = async () => {
    const toastrKey = this.getTranslatedTitle();
    this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.Uploading, true, toastrKey);

    // in case user deletes file on gdrive after opening it into Asgard
    const isFileTrashedOrGone = await this.checkIfFileIsTrashedOrGone();
    if (isFileTrashedOrGone) {
      this.uploadInfo.fileId = undefined;
    }

    if (this.uploadInfo.fileId) {
      this.cloudRecentFilesService.removeFileFromRecent(this.uploadInfo.fileId, CloudServiceEnum.GoogleDrive);
    }

    const uploader = new UploaderForGoogleDrive(this.uploadInfo);
    await uploader.upload();

    if (this.tabService.activeTab && this.tabService.activeTab.isOriginallyKES && this.uploadInfo.file.type !== MimeTypesEnum.Mp3) {
      // this.tabService.activeTab.isDedicon ?
      //   this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.PDF_SEK_SAVE_SUCCESS' }) :
      //   this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.PDF_KES_SAVE_SUCCESS' });

        const messageKey = this.tabService.activeTab.isDedicon ? 'COMMONMESSAGEDIALOG.PDF_SEK_SAVE_SUCCESS' : 'COMMONMESSAGEDIALOG.PDF_KES_SAVE_SUCCESS';
        this.toastrService.showToaster(ToastrActionEnum.Error, this.translateService.instant(messageKey));
      }
  }

  checkIfFileIsTrashedOrGone = async (): Promise<boolean | undefined> => {
    if (this.driveMetadata === null) {
      return true;
    }

    const fields = 'trashed';
    return this.gdriveApi.getFile(this.driveMetadata.id, fields)
      .then((file) => {
        return file.trashed;
      })
      .catch((error) => {
        console.error(error);
        this.displayGeneralError();
        return true;
      });
  }

  getFileFromId(fileId: string): Promise<File> {
    return this.getFile(fileId, SaveStatusEnum.ExamModeFilePicker)
      .then((file) => {
        this.toastrService.hideToastr(file.name);
        return Promise.resolve(file);
      })
      .catch((error) => {
        console.error(error);
        this.displayGeneralError();
        const metadata = this.getMetadata(fileId);
        this.toastrService.hideToastr(metadata === undefined ? '' : metadata.name);
        return Promise.reject();
      });
  }

  private onImportFailed = (res: string) => {
    const toastrKey = this.getTranslatedTitle();
    this.toastrService.hideToastr(toastrKey);
    if (res) {
      const parsedError: GDriveSaveFileError = JSON.parse(res);
      if (parsedError && parsedError.error && parsedError.error.message) {
        // added translation for 403
        if (parsedError.error.code === 403 || parsedError.error.code === 507) {
          this.displayError(new CloudException(parsedError.error.code.toString()));
        } else {
          // show regular not translated error message
          this.displayError(parsedError.error.message);
        }
      } else {
        // show unknown error
        this.displayError('ERRORS.0');
      }
    }
  }

  private onImportComplete = (res): void => {
    const toastrKey = this.getTranslatedTitle();
    const response = JSON.parse(res);
    // Don't add .sek/.kes import/export metadata to the tab. They shouldn't be linked to the converted pdf file.
    if (response.mimeType !== MimeTypesEnum.ApplicationOctetStream
      && response.mimeType !== MimeTypesEnum.KesNoType
      && response.mimeType !== MimeTypesEnum.Mp3) {

      this.driveMetadata = {
        type: MetadataTypesEnum.DriveMetadata,
        name: response.name,
        id: response.id,
        mimeType: response.mimeType,
        parents: response.parentId
      };

      this.cloudRecentFilesService.addFileToRecent(this.driveMetadata, CloudServiceEnum.GoogleDrive);

      this.driveMetadata.type = MetadataTypesEnum.DriveMetadata;
      this.fillMetadata(this.driveMetadata);

      this.overrideFilenameParams(this.driveMetadata.name);
    }

    this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.UploadDone, false, toastrKey);
  }

  private validateFileSupport(metadata: DriveMetadata, dialogStatus: SaveStatusEnum): boolean {
    const vMetadata = { fileName: metadata.name, mimeType: metadata.mimeType };

    // if the item is a shortcut
    if (metadata.mimeType === MimeTypesEnum.GoogleDriveShortcut) {
      vMetadata.mimeType = metadata.shortcutDetails?.targetMimeType;
    }

    const isOcr = dialogStatus === SaveStatusEnum.Ocr;
    return validateMimetypes(vMetadata, isOcr, true);
  }

  private displayGeneralError(): void {
    this.displayError(new CloudException(CloudExceptionEnum.fileRetrieval));
  }

  private mapMetadataToCloudData(metadata: DriveMetadata): CloudData {
    const extension = parseExtenstion(metadata.name || '');
    let mimeType = metadata.mimeType === MimeTypesEnum.DriveFolder ?
      MimeTypesEnum.Folder : getMimeTypeEnumFromString(metadata.mimeType || '');

    // or if mimetype was the new shortcuts
    if (metadata.mimeType === MimeTypesEnum.GoogleDriveShortcut) {
      mimeType = MimeTypesEnum.GoogleDriveShortcut;
    }

    const cloudData: CloudData = {
      id: metadata.id || '',
      name: metadata.name || '',
      extension: mimeType !== MimeTypesEnum.Folder ? extension : '',
      mimeType,
      modifiedTime: metadata.modifiedTime || new Date().toJSON(),
      isShared: metadata.shared || false,
      size: metadata.size ? metadata.size.toString() : '',
      path: metadata.id || '',
      parents: metadata.parents ? metadata.parents[0] : '',
      shortcutDetails: metadata.shortcutDetails,
      capabilities: {
        canRename: metadata.capabilities?.canRename ?? true,
        // google drive does not support copying folders
        canCopy:  mimeType === MimeTypesEnum.Folder ? true : (metadata.capabilities?.canCopy ?? true),
        canMove: metadata.capabilities?.canMoveItemWithinDrive ?? true,
        canRemove: metadata.capabilities?.canDelete ?? true
      }
    };

    return cloudData;
  }

  private async getFile(fileId: string, dialogStatus: SaveStatusEnum): Promise<File> {
    const metadata = this.getMetadata(fileId);
    if (!metadata) {
      return Promise.reject();
    }

    if (!this.validateFileSupport(metadata, dialogStatus)) {
      // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.NOT_SUPPORTED' });
      this.toastrService.showToaster(ToastrActionEnum.Error, this.translateService.instant('COMMONMESSAGEDIALOG.NOT_SUPPORTED'));
      return Promise.reject();
    }

    this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.Downloading, true, metadata.name);

    const token = await this.sessionService.getUserToken();
    const header = {
      headers: new HttpHeaders().set('Authorization', `Bearer ${token}`),
      responseType: 'blob'
    };

    // to support the new shortcuts
    const id = metadata.shortcutDetails ? metadata.shortcutDetails.targetId : metadata.id;

    // @ts-ignore
    const dataFile$ = this.http.get(`https://www.googleapis.com/drive/v3/files/${id}?alt=media`, header);

    let fileExtension: MimeTypesEnum | null | undefined = null;

    if (metadata && metadata.mimeType === MimeTypesEnum.ApplicationMsWord && metadata.name.match(this.KES_OR_SEK_PATTERN)) {
      fileExtension = MimeTypesEnum.ApplicationOctetStream;
    } else if (metadata) {
      fileExtension = metadata.mimeType;
    }

    const userId = this.accountService.userId;

    return new Promise((resolve, reject) => {
      dataFile$.subscribe(
        blob => {
          // if it is still the same user that is logged in
          const currentUserId = this.accountService.userId;
          if (userId !== currentUserId) {
            // hide de downloading toastr again
            this.toastrService.hideToastr(metadata.name);
            reject('different user');
          }

          if (fileExtension && metadata) {
            const file = this.createFileFromBlob(blob, fileExtension, metadata.name);
            resolve(file);
          }
        },
        _ => {
          reject();
        }
      );
    });
  }
}
